// Phase 7 — Call Intelligence inline panel for Call History.
//
// Renders a single call_intelligence record for the given callId.
// If no record exists yet, offers a "Run analysis" button (editors only).
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { callIntelligenceApi } from '../../api';
import {
  SENTIMENT_COLORS,
  READINESS_COLORS,
  CONFIDENCE_COLORS,
  ANALYSIS_STATUS_COLORS,
  OBJECTION_LABELS,
  QA_FLAG_LABELS,
} from './constants';
import TasksForCall from './TasksForCall';

function Pill({ label, colorClass }) {
  if (!label) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass || 'bg-gray-100 text-gray-700'}`}>
      {label}
    </span>
  );
}

function ScoreBar({ label, value }) {
  const v = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : null;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">{v == null ? '—' : `${v}`}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-primary/70" style={{ width: `${v ?? 0}%` }} />
      </div>
    </div>
  );
}

export default function CallIntelligencePanel({ callId, isEditor, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchOne = useCallback(async () => {
    if (!callId) return;
    setLoading(true);
    try {
      const res = await callIntelligenceApi.list({ call_id: callId, limit: 1 });
      const items = res?.items || [];
      setRecord(items[0] || null);
    } catch (err) {
      toast.error(err?.message || 'Failed to load analysis');
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  const rerun = async () => {
    if (!callId) return;
    setBusy(true);
    try {
      const res = await callIntelligenceApi.rerun(callId);
      if (res?.status === 'completed' || res?.status === 'pending' || res?.status === 'queued') {
        toast.success(`Analysis ${res.status}`);
      } else if (res?.status === 'skipped') {
        toast.message(`Analysis skipped${res.reason ? `: ${res.reason}` : ''}`);
      } else if (res?.status === 'failed') {
        toast.error(`Analysis failed${res.reason ? `: ${res.reason}` : ''}`);
      } else {
        toast.success('Analysis started');
      }
      await fetchOne();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.message || 'Failed to run analysis');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading analysis…
      </div>
    );
  }

  if (!record) {
    return (
      <div className="border rounded-lg p-4 text-sm text-muted-foreground">
        <p className="mb-3">No AI analysis yet for this call.</p>
        {isEditor && (
          <Button size="sm" variant="outline" onClick={rerun} disabled={busy} className="gap-2" data-testid={`ci-run-${callId}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Run analysis
          </Button>
        )}
      </div>
    );
  }

  const status = record.analysis_status;
  const degraded = status === 'failed' || status === 'skipped';
  const objections = Array.isArray(record.objection_types) ? record.objection_types : [];
  const qaFlags = Array.isArray(record.qa_flags) ? record.qa_flags : [];

  return (
    <div className="border rounded-lg p-4 space-y-4">
      {/* Top pills */}
      <div className="flex flex-wrap items-center gap-2">
        <Pill label={record.sentiment} colorClass={SENTIMENT_COLORS[record.sentiment]} />
        <Pill label={record.appointment_readiness && `Readiness: ${record.appointment_readiness}`} colorClass={READINESS_COLORS[record.appointment_readiness]} />
        <Pill label={record.confidence_level && `Confidence: ${record.confidence_level}`} colorClass={CONFIDENCE_COLORS[record.confidence_level]} />
        <Pill label={status} colorClass={ANALYSIS_STATUS_COLORS[status]} />
      </div>

      {/* Summary */}
      {record.summary && (
        <div className="p-3 rounded-md bg-muted/50 text-sm">
          {record.summary}
        </div>
      )}

      {degraded && !record.summary && (
        <div className="p-3 rounded-md bg-muted/50 text-sm text-muted-foreground">
          Analysis {status}{record.reason ? `: ${record.reason}` : '.'}
        </div>
      )}

      {/* Scores */}
      {!degraded && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ScoreBar label="Buyer intent" value={record.buyer_intent_score} />
          <ScoreBar label="Urgency" value={record.urgency_score} />
          <ScoreBar label="Lead quality" value={record.lead_quality_score} />
        </div>
      )}

      {/* Objections */}
      {objections.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-1">Objections</div>
          <div className="flex flex-wrap gap-1.5">
            {objections.map((o) => (
              <Pill key={o} label={OBJECTION_LABELS[o] || o} colorClass="bg-slate-100 text-slate-700" />
            ))}
          </div>
        </div>
      )}

      {/* QA flags */}
      {qaFlags.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-1">QA flags</div>
          <div className="flex flex-wrap gap-1.5">
            {qaFlags.map((f) => (
              <Pill key={f} label={QA_FLAG_LABELS[f] || f} colorClass="bg-rose-100 text-rose-800" />
            ))}
          </div>
        </div>
      )}

      {/* Next best action */}
      {record.next_best_action && (
        <blockquote className="border-l-4 border-primary/40 pl-3 text-sm italic text-foreground/90">
          {record.next_best_action}
        </blockquote>
      )}

      {/* Generated tasks */}
      {record.call_id && (
        <div className="pt-2 border-t">
          <div className="text-xs text-muted-foreground mb-2">Generated tasks</div>
          <TasksForCall callId={record.call_id} />
        </div>
      )}

      {/* Re-analyze */}
      {isEditor && (
        <div className="pt-2 border-t flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={rerun} disabled={busy} className="gap-2" data-testid={`ci-rerun-${callId}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-analyze
          </Button>
          {record.model_provider && record.model_name && (
            <span className="text-xs text-muted-foreground font-mono">
              {record.model_provider}/{record.model_name}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
